import { useState, useEffect } from "react"
import { FormModal } from "@/components/ui/form-modal"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useEditUser } from "@/hooks/useEditUser"
import { useCondominiums } from "@/hooks/useCondominiums"
import { Tables } from "@/integrations/supabase/types"
import { Edit } from "lucide-react"

type Profile = Tables<"profiles">

interface EditUserModalProps {
  user: Profile | null
  isOpen: boolean
  onClose: () => void
}

const roleOptions = [
  { value: 'morador', label: 'Morador' },
  { value: 'porteiro', label: 'Porteiro' },
  { value: 'sindico', label: 'Síndico' },
  { value: 'administracao', label: 'Administração' },
  { value: 'superadmin', label: 'Super Admin' },
]

export const EditUserModal = ({ user, isOpen, onClose }: EditUserModalProps) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'morador' as Profile['role'],
    condominium_id: '',
    is_active: true,
  })

  const editUserMutation = useEditUser()
  const { data: condominiums } = useCondominiums()

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        password: '',
        role: user.role,
        condominium_id: user.condominium_id || 'none',
        is_active: user.is_active,
      })
    }
  }, [user, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) return

    const updateData: any = {
      id: user.id,
      full_name: formData.full_name,
      role: formData.role,
      condominium_id: formData.condominium_id === 'none' ? null : formData.condominium_id,
      is_active: formData.is_active,
    }

    // Só incluir email se foi alterado
    if (formData.email !== user.email) {
      updateData.email = formData.email
    }

    // Só incluir senha se foi preenchida
    if (formData.password.trim()) {
      updateData.password = formData.password
    }

    try {
      await editUserMutation.mutateAsync(updateData)
      onClose()
    } catch (error) {
      // Erro já é tratado no hook
    }
  }

  const formContent = (
    <>
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="full_name">Nome Completo</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
            placeholder="Digite o nome completo"
            required
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="Digite o email"
            required
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="password">Nova Senha (opcional)</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            placeholder="Digite uma nova senha (deixe em branco para não alterar)"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="role">Função</Label>
          <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value as Profile['role'] }))}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a função" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="condominium">Condomínio</Label>
          <Select value={formData.condominium_id} onValueChange={(value) => setFormData(prev => ({ ...prev, condominium_id: value }))}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o condomínio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sem condomínio</SelectItem>
              {condominiums?.map((condominium) => (
                <SelectItem key={condominium.id} value={condominium.id}>
                  {condominium.name} ({condominium.type === "ponto_eletronico" ? "Ponto Eletrônico" : "Condomínio"})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="is_active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
          />
          <Label htmlFor="is_active">Usuário ativo</Label>
        </div>
      </div>
    </>
  )

  return (
    <FormModal
      title="Editar Usuário"
      description="Altere os dados do usuário selecionado."
      open={isOpen}
      onOpenChange={onClose}
      onSubmit={handleSubmit}
      submitButtonText="Salvar alterações"
      loading={editUserMutation.isPending}
      formContent={formContent}
      triggerButton={
        <Button variant="outline" size="sm">
          <Edit className="h-4 w-4" />
        </Button>
      }
    />
  )
}